import { errorToast } from '@libs/helper';
import type { ReactNode } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

const sanitizeFileName = (fileName: string, replacement?: string): string => {
  replacement = replacement ?? '_';

  const sanitized = fileName
    .replace(/[\\/?<>:*|"'#$%&]/g, replacement)
    .replace(/^\.+/, replacement)
    .replace(/^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i, replacement)
    .replace(/[. ]+$/, replacement)
    .replace(new RegExp(`${replacement}{2,}`, 'g'), replacement);

  return sanitized.slice(0, 255);
};

const validateFiles = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (
    ext &&
    [
      '.exe',
      '.bat',
      '.sh',
      '.js',
      '.vbs',
      '.scr',
      '.cmd',
      '.msi',
      '.html',
      '.htm',
      '.svg',
      '.php',
      '.ts',
      '.js',
      '.jsx',
      '.tsx',
    ].includes(`.${ext}`)
  ) {
    return false;
  }
  return true;
};

interface DropzoneProps extends DropzoneOptions {
  onDrop: (acceptedFiles: File[]) => void;
  children: ReactNode;
  className?: string;
}

function Dropzone({ onDrop, children, className, ...rest }: DropzoneProps): JSX.Element {
  const handleDrop = (files: File[]) => {
    const validFiles = files.filter(validateFiles);
    if (validFiles.length > 0) {
      const sanitizedFiles = validFiles.map((file) => {
        const sanitizedFileName = sanitizeFileName(file.name);
        return new File([file], sanitizedFileName, { type: file.type });
      });
      onDrop(sanitizedFiles);
    } else {
      errorToast('Some files were rejected due to security concerns.');
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    ...rest,
  });
  return (
    <div className={className} {...getRootProps()}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
}

export default Dropzone;
